import { useState } from 'react'
import { useCourseLibrary, type LibraryCourse } from '@/hooks/useCourseLibrary'
import { SecureAssetViewer } from '@/components/curriculum/SecureAssetViewer'
import { AssetThumbnail } from '@/components/curriculum/AssetThumbnail'

const emptyStyle: React.CSSProperties = {
  background: '#fff', border: '1px dashed rgba(27,42,74,0.15)', borderRadius: 14,
  padding: '40px 28px', textAlign: 'center', color: 'rgba(27,42,74,0.45)',
}
const cardStyle: React.CSSProperties = {
  background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14,
  padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12,
}

// Browse every course's current content, grouped by licence class — what was previously
// a placeholder stub. Each file shows as a real thumbnail (page 1 for a PDF, first frame
// for a video), not just a text button — click it to open the full SecureAssetViewer
// (same component used for assigned-lesson preview in CurriculumAssignmentPanel.tsx,
// streaming-only, no download link).
export default function Library() {
  const { courses, loading, error } = useCourseLibrary()
  const [preview, setPreview] = useState<{ assetId: string; assetType: 'pdf' | 'video'; title: string } | null>(null)
  const [licenceClass, setLicenceClass] = useState<'G2' | 'G'>('G2')

  const g2 = courses.filter(c => c.licenceClass === 'G2')
  const g = courses.filter(c => c.licenceClass === 'G')
  const active = licenceClass === 'G2' ? g2 : g

  return (
    <div>
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>Library</div>
      <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 20 }}>
        Every published course's current PDF and video.
      </div>

      <div style={{
        display: 'inline-flex', gap: 2, padding: 3, marginBottom: 20,
        background: 'rgba(27,42,74,0.06)', borderRadius: 10,
      }}>
        {(['G2', 'G'] as const).map(lc => (
          <button
            key={lc}
            onClick={() => setLicenceClass(lc)}
            style={{
              padding: '8px 22px', border: 'none', borderRadius: 7,
              background: licenceClass === lc ? '#fff' : 'none',
              boxShadow: licenceClass === lc ? '0 1px 3px rgba(27,42,74,0.15)' : 'none',
              font: licenceClass === lc ? '600 13.5px Inter, sans-serif' : '500 13.5px Inter, sans-serif',
              color: licenceClass === lc ? '#0F7C6C' : 'rgba(27,42,74,0.55)', cursor: 'pointer',
            }}
          >
            {lc} ({lc === 'G2' ? g2.length : g.length})
          </button>
        ))}
      </div>

      {error && <div className='banner-error' style={{ marginBottom: 14 }}>{error}</div>}
      {loading && <div style={{ color: 'rgba(27,42,74,0.4)' }}>Loading…</div>}

      {!loading && !error && (
        <CourseGroup title={licenceClass} courses={active} onPreview={setPreview} />
      )}

      {preview && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(27,42,74,0.6)', zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
          }}
          onClick={() => setPreview(null)}
        >
          <div style={{ width: '100%', maxWidth: 820 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ font: '600 14px Inter, sans-serif', color: '#fff' }}>{preview.title}</div>
              <button onClick={() => setPreview(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>
            <SecureAssetViewer assetId={preview.assetId} assetType={preview.assetType} title={preview.title} />
          </div>
        </div>
      )}
    </div>
  )
}

function CourseGroup({
  title, courses, onPreview,
}: {
  title: string
  courses: LibraryCourse[]
  onPreview: (p: { assetId: string; assetType: 'pdf' | 'video'; title: string }) => void
}) {
  if (courses.length === 0) {
    return <div style={emptyStyle}>No {title} courses published yet.</div>
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 320px))', gap: 14 }}>
      {courses.map(c => (
        <div key={c.courseId} style={cardStyle}>
          <div style={{ font: '600 14.5px Inter, sans-serif', color: '#1B2A4A' }}>{c.title}</div>
          {(c.pdfAssetId || c.videoAssetId) ? (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {c.pdfAssetId && (
                <AssetThumbnail
                  assetId={c.pdfAssetId} assetType='pdf' title={`${c.title} — PDF`}
                  onClick={() => onPreview({ assetId: c.pdfAssetId!, assetType: 'pdf', title: `${c.title} — PDF` })}
                />
              )}
              {c.videoAssetId && (
                <AssetThumbnail
                  assetId={c.videoAssetId} assetType='video' title={`${c.title} — Video`}
                  onClick={() => onPreview({ assetId: c.videoAssetId!, assetType: 'video', title: `${c.title} — Video` })}
                />
              )}
            </div>
          ) : (
            <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>No content yet</div>
          )}
        </div>
      ))}
    </div>
  )
}
